/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// react-router-dom components
import { useLocation, NavLink, Link as RouterLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAvatar from "components/MDAvatar";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import { canShowAdmin } from "services/categories";
import { FUNCTIONS, hasFunction } from "shared/accessPermissions";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";
import { signOutFirebase } from "services/firebase";

function Sidenav({ color, brand, brandName, routes, user, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const location = useLocation();
  const isAccountArea = location.pathname === "/account";
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState("");

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);
  const userName = user?.displayName || user?.email || "Usuario conectado";
  const userEmail = user?.email || "Sessao ativa";
  const userPhoto = user?.photoURL || "";
  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    setLogoutError("");
    try {
      await signOutFirebase();
      setLogoutOpen(false);
    } catch {
      setLogoutError("Não foi possível sair da conta. Tente novamente.");
    } finally {
      setLoggingOut(false);
    }
  };

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(
    ({ type, name, icon, title, noCollapse, key, href, route, adminOnly }) => {
      if (adminOnly && !canShowAdmin(user)) return null;
      if (user?.access && Object.hasOwn(FUNCTIONS, key) && !hasFunction(user.access, key))
        return null;
      let returnValue;

      if (key === "sign-in" && user) {
        return null;
      }

      if (isAccountArea && ["dashboard", "tables", "billing"].includes(key)) {
        return null;
      }

      if (type === "collapse") {
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse name={name} icon={icon} active={false} noCollapse={noCollapse} />
          </Link>
        ) : (
          <NavLink key={key} to={route}>
            {({ isActive }) => <SidenavCollapse name={name} icon={icon} active={isActive} />}
          </NavLink>
        );
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }

      return returnValue;
    }
  );

  const accountRoutes = user && isAccountArea && !miniSidenav && (
    <MDBox px={2} pb={1}>
      <MDTypography
        variant="caption"
        fontWeight="bold"
        color={textColor}
        textTransform="uppercase"
        sx={{ opacity: 0.7, display: "block", mb: 1.25, px: 1 }}
      >
        Minha conta
      </MDTypography>
      <NavLink to="/account?tab=profile">
        <SidenavCollapse
          name="Perfil"
          icon={<PersonRoundedIcon fontSize="small" />}
          active={location.pathname === "/account" && !location.search.includes("tab=connections")}
        />
      </NavLink>
      <NavLink to="/account?tab=connections">
        <SidenavCollapse
          name="Conexoes"
          icon={<LinkRoundedIcon fontSize="small" />}
          active={location.pathname === "/account" && location.search.includes("tab=connections")}
        />
      </NavLink>
    </MDBox>
  );

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={miniSidenav ? 1 : 4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox
          component={NavLink}
          to="/dashboard"
          display="flex"
          justifyContent="center"
          alignItems="center"
          aria-label={brandName}
        >
          {brand ? (
            <MDBox
              component="img"
              src={brand}
              alt={brandName}
              width={miniSidenav ? 40 : 104}
              sx={{
                height: "auto",
                maxWidth: "100%",
                borderRadius: "8px",
                backgroundColor: "#fff",
              }}
            />
          ) : (
            <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
              {brandName}
            </MDTypography>
          )}
        </MDBox>
        {user && !miniSidenav && (
          <MDBox
            component={RouterLink}
            to="/account?tab=profile"
            mt={2}
            px={2}
            py={1.5}
            borderRadius="xl"
            bgColor="rgba(255,255,255,0.08)"
            display="flex"
            alignItems="center"
            textAlign="left"
            sx={{
              backdropFilter: "blur(8px)",
              textDecoration: "none",
              transition: "transform 180ms ease, background-color 180ms ease",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.12)",
                transform: "translateY(-1px)",
              },
            }}
          >
            <MDAvatar src={userPhoto} alt={userName} size="md" shadow="md" sx={{ flexShrink: 0 }} />
            <MDBox ml={1.5} minWidth={0} sx={{ flex: 1, overflow: "hidden" }}>
              <MDTypography
                variant="button"
                color={textColor}
                fontWeight="medium"
                title={userName}
                sx={{
                  display: "block",
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {userName}
              </MDTypography>
              <MDTypography
                variant="caption"
                color={textColor}
                title={userEmail}
                sx={{
                  display: "block",
                  opacity: 0.8,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
              >
                {userEmail}
              </MDTypography>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>
      {accountRoutes}
      {user && isAccountArea && (
        <MDBox px={2} pb={1}>
          <MDButton
            component={RouterLink}
            to="/dashboard"
            fullWidth
            startIcon={<DashboardRoundedIcon />}
            sx={{
              justifyContent: miniSidenav ? "center" : "flex-start",
              px: miniSidenav ? 1.5 : 2,
              py: 1.2,
              borderRadius: "0.75rem",
              color: "#d7e9ff",
              backgroundColor: "rgba(18, 191, 56, 0.16)",
              border: "1px solid rgba(18, 191, 56, 0.28)",
              boxShadow: "none",
              "& .MuiButton-startIcon": {
                marginRight: miniSidenav ? 0 : 1.25,
                marginLeft: 0,
              },
              "&:hover": {
                backgroundColor: "rgba(18, 191, 56, 0.24)",
                borderColor: "rgba(18, 191, 56, 0.4)",
                boxShadow: "none",
              },
            }}
          >
            {!miniSidenav && "Dashboard"}
          </MDButton>
        </MDBox>
      )}
      {user && (
        <MDBox px={2} pb={2} mt={3}>
          <MDButton
            fullWidth
            aria-label="Sair da plataforma"
            onClick={() => {
              setLogoutError("");
              setLogoutOpen(true);
            }}
            startIcon={<LogoutRoundedIcon />}
            sx={{
              justifyContent: miniSidenav ? "center" : "flex-start",
              px: miniSidenav ? 1.5 : 2,
              py: 1.2,
              borderRadius: "0.75rem",
              color: "#ffd7d7",
              backgroundColor: "rgba(255, 102, 102, 0.16)",
              border: "1px solid rgba(255, 138, 138, 0.28)",
              boxShadow: "none",
              "& .MuiButton-startIcon": {
                marginRight: miniSidenav ? 0 : 1.25,
                marginLeft: 0,
              },
              "&:hover": {
                backgroundColor: "rgba(255, 102, 102, 0.24)",
                borderColor: "rgba(255, 160, 160, 0.4)",
                boxShadow: "none",
              },
            }}
          >
            {!miniSidenav && "Sair"}
          </MDButton>
        </MDBox>
      )}
      <Dialog
        open={logoutOpen}
        onClose={() => {
          if (!loggingOut) setLogoutOpen(false);
        }}
        disableEscapeKeyDown={loggingOut}
        fullWidth
        maxWidth="xs"
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle id="logout-dialog-title" sx={{ color: "#182d27", fontWeight: 700 }}>
          Sair da plataforma?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description" sx={{ fontSize: 15, lineHeight: 1.7 }}>
            Deseja realmente sair da sua conta? Para voltar, você precisará entrar novamente.
          </DialogContentText>
          {logoutError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {logoutError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            gap: 1,
            flexWrap: "wrap",
            "& .MuiButton-root": {
              minHeight: 44,
              borderRadius: 2,
              margin: "0 !important",
              flex: { xs: "1 1 100%", sm: "0 0 auto" },
            },
          }}
        >
          <Button
            autoFocus
            disabled={loggingOut}
            color="success"
            variant="outlined"
            onClick={() => setLogoutOpen(false)}
          >
            Continuar na plataforma
          </Button>
          <Button
            disabled={loggingOut}
            color="error"
            variant="contained"
            onClick={handleLogout}
            startIcon={
              loggingOut ? <CircularProgress color="inherit" size={16} /> : <LogoutRoundedIcon />
            }
          >
            {loggingOut ? "Saindo…" : "Sair da conta"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* <MDBox p={2} mt="auto">
        <MDButton
          component="a"
          href="https://www.creative-tim.com/product/material-dashboard-pro-react"
          target="_blank"
          rel="noreferrer"
          variant="gradient"
          color={sidenavColor}
          fullWidth
        >
          upgrade to pro
        </MDButton>
      </MDBox> */}
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  user: PropTypes.shape({
    displayName: PropTypes.string,
    email: PropTypes.string,
    photoURL: PropTypes.string,
  }),
};

export default Sidenav;
